/**
 * TumPersonellerPage.jsx — Kişi Listesi (denetmen/admin tarafı)
 * Route: /Management/Kisi/Personeller  (?gorunum=fuar · ?q= · ?sayfa= · süzgeç parametreleri · ?yeni=1&firmaId=)
 * ⚠️ Route adı BİLİNÇLİ olarak eski: /Kisi/Yeni ve /Kisi/KimlikDogrulama yönlendirmelerinin
 * hedefi ve dışarıda paylaşılmış adres — sayfanın adı değişse de yolu değiştirmeyin.
 *
 * Tek uç: GET /personnel/kisi-listesi (services/kisiListService.js).
 *   · İki düğme (#260): "Tüm Kişiler" (varsayılan; sistemdeki her kişi, hiç görevlendirilmemişler dahil)
 *     ve "Fuara Eşlenmiş" (= ÜST ÇUBUKTAKİ fuarda görevli herkes, tüm firmalar). Fuar düğmesi sayfa içi
 *     ayrı bir fuar seçmez, üst çubuğu izler — "Personel Ekle" fuarı backend'de oturumdan (üst çubuk)
 *     alıyor; ikisi ayrışırsa kişi başka fuara alınırdı. Başka fuarın kişilerini görmek için üst çubuktaki
 *     fuar değiştirilir.
 *   · Belge kareleri her iki görünümde de gelir (belgeler=true).
 *   · Görünüm, arama, süzgeçler ve sayfa adres çubuğunda tutulur (#262, kisiListService.listeDurumu*):
 *     kişi detayından dönüşte hepsi, kaydırma konumuyla birlikte yerinde durur. Detaya giderken
 *     `state: { from, scrollY }` verilir; detay "geri" bağlantısını ondan kurar.
 * Arama (ad / T.C. no / firma) ve "Filtreler" paneli sunucu tarafında, sayfalamadan ÖNCE çalışır;
 * istemci tarafında süzme/önbellek yok.
 *
 * Eskiden ayrı sayfa olan iki iş buraya taşındı:
 *   · Personel Ekle             → "Personel Ekle" popup'ı (Components/Kisi/PersonelEkleModal)
 *   · Fuara Eşle (#261)         → satırdaki kişiyi ÜST ÇUBUKTAKİ fuara eşler: aynı popup, `bilinenKisi` ile (kimlik
 *                               sorgusu yok). Kart Yazdır penceresi "fuarda görevli değil" derse oradan da açılır.
 *   · Kimlik Doğrulama        → satır eylemi (Components/Kisi/KimlikDogrulamaModal); kartta "Kimlik"
 *                               düğmesi ya da vesikalık kutusu açar (#131)
 *   · Meslek düzenleme (#136) → kartta firmanın altındaki "Meslek" düğmesi (MeslekDuzenleModal)
 *
 * Görevlendirme / Eşleme sayıları (#247): fuar seçiliyken başlığın yanında, GET /eslesme-istatistik.
 * "Tüm kişiler"de fuar yok, sayı da gösterilmez. SAKA onay kuyruğu (Firmaya Eşleme Talepleri) backend
 * #112 ile silindi; sayfası, düğmesi ve rozeti de gitti.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Card, CardBody, Row, Col, Button, ButtonGroup, Badge, Spinner, Alert, Input, Collapse,
  Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { UserPlus, Search, SlidersHorizontal } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import FairContext from "../../../Components/Contexts/Management/FairContext";
import KimlikDogrulamaModal from "../../../Components/Kisi/KimlikDogrulamaModal";
import KisiFiltreleri from "../../../Components/Kisi/KisiFiltreleri";
import KartYazdirModal from "../../../Components/Kisi/KartYazdirModal";
import PanelDavetiModal from "../../../Components/Kisi/PanelDavetiModal";
import MeslekDuzenleModal from "../../../Components/Kisi/MeslekDuzenleModal";
import PersonelKart from "../../../Components/Kisi/PersonelKart";
import { PersonelLegendButton } from "../../../Components/Kisi/PersonelKartParcalari";
import { onFirmaSec } from "../../../Components/Kisi/PersonelFirmaBelgeParcalari";
import PersonelEkleModal, { kisiSatirindanBilinenKisi } from "../../../Components/Kisi/PersonelEkleModal";
import {
  BOS_SUZGEC, aktifSuzgecSayisi, getKisiListesi, listeDurumuUrldenOku, listeDurumunuUrleYaz,
} from "../../../services/kisiListService";
import { getEslesmeIstatistik } from "../../../services/personelRosterService";
import { useTranslation } from "react-i18next";
import { currentLocaleTag } from "../../../i18n";
import i18n from "../../../i18n";

const PAGE_SIZE = 10;
const SUZGEC_DEBOUNCE_MS = 400;

/** Eski /Kisi/Yeni ve /Kisi/KimlikDogrulama adreslerini sorgu dizesini koruyarak buraya yönlendirir. */
export function KisiPersonellerRedirect({ extra }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  Object.entries(extra || {}).forEach(([k, v]) => params.set(k, v));
  return <Navigate to={`/Management/Kisi/Personeller?${params.toString()}`} replace />;
}

/** Aktif sayfanın çevresinde en fazla 7 sayfa numarası (sistem geneli liste binlerce sayfa olabilir). */
function sayfaNumaralari(aktif, toplam) {
  const bas = Math.max(1, Math.min(aktif - 3, toplam - 6));
  const son = Math.min(toplam, bas + 6);
  return Array.from({ length: son - bas + 1 }, (_, i) => bas + i);
}

export default function TumPersonellerPage() {
  const { t: tx } = useTranslation("yonetim");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const { getFairs } = useContext(FairContext);

  // Liste durumu adres çubuğundan YALNIZCA açılışta okunur (#262); sonrasında state asıl, URL onun yansıması.
  const [ilk] = useState(() => listeDurumuUrldenOku(searchParams));
  // Detaydan dönüşte kaydırma konumu (Link state'i ya da tarayıcı geri tuşu için bu girdinin kendi state'i).
  const bekleyenKaydirma = useRef(typeof location.state?.scrollY === "number" ? location.state.scrollY : null);

  const [fuarlar, setFuarlar] = useState([]);
  // Açılış görünümü: Tüm Kişiler. "Fuara Eşlenmiş" ayrı bir fuar seçmez, üst çubuktaki fuarı izler (#260).
  const [fuarGorunumu, setFuarGorunumu] = useState(ilk.fuarGorunumu);
  const [aramaInput, setAramaInput] = useState(ilk.arama);
  const [arama, setArama] = useState(ilk.arama);
  // WAS (until #478): const [firmaTumFuarlar, setFirmaTumFuarlar] = useState(ilk.firmaTumFuarlar); — "Diğer fuarlardaki firmalar dahil"
  // onay kutusu (?tumFuarlar=1, firmaTumFuarlar parametresi) kalktı: Tüm Kişiler + #477'nin gri düğmesi "bu fuarda kim var"ı söylüyor.
  // Uç parametreyi hâlâ kabul eder; yalnızca bu sayfa göndermiyor.
  // `suzgec` panelde düzenlenen, `uygulananSuzgec` debounce sonrası isteğe giden (ve URL'ye yazılan) hali.
  const [suzgec, setSuzgec] = useState(ilk.suzgec);
  const [uygulananSuzgec, setUygulananSuzgec] = useState(ilk.suzgec);
  const [filtreAcik, setFiltreAcik] = useState(() => aktifSuzgecSayisi(ilk.suzgec, { fuarId: ilk.fuarGorunumu ? 1 : null, aktifFuarId: 1 }) > 0);
  const [page, setPage] = useState(ilk.sayfa);

  const [sayfa, setSayfa] = useState({ records: [], total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [kimlikHedef, setKimlikHedef] = useState(null);
  const [kartHedef, setKartHedef] = useState(null);
  // #647: "Panele davet et" (SMS ile geçici kişi paneli, backend #644): davet penceresinin kişisi
  const [davetHedef, setDavetHedef] = useState(null);
  const [esleHedef, setEsleHedef] = useState(null); // Fuara Eşle (#261): satırdaki kişi
  const [meslekHedef, setMeslekHedef] = useState(null); // { person, firma, fuarId }
  const [istatistik, setIstatistik] = useState(null);
  const [yeniAcik, setYeniAcik] = useState(searchParams.get("yeni") === "1");
  const initialFirmaId = Number(searchParams.get("firmaId")) || null;

  // Fuar görünümünün fuarı HER ZAMAN üst çubuktaki fuardır (Personel Ekle de onu kullanır, #260).
  // Adres `?gorunum=fuar` ile açıldı ama üst çubuk fuarı henüz çözülmediyse (girişten sonra) beklenir.
  const ustCubukFuarId = Number(selectedFuarId) || null;
  const fuarId = fuarGorunumu ? ustCubukFuarId : null;
  const fuarBekleniyor = fuarGorunumu && !ustCubukFuarId;
  const aktifSayi = aktifSuzgecSayisi(suzgec, { fuarId, aktifFuarId: ustCubukFuarId });

  useEffect(() => { document.title = i18n.t("yonetim:tumPersoneller.kisiListesiFds"); }, []);

  // Üst çubuktaki fuar değişirse fuar görünümünün sayfa numarası geçersiz kalır. İlk çözümlenmeyi
  // (null → fuar) saymaz: adresten gelen sayfa korunmalı.
  const oncekiFuarId = useRef(fuarId);
  useEffect(() => {
    if (oncekiFuarId.current && fuarId && oncekiFuarId.current !== fuarId) setPage(1);
    oncekiFuarId.current = fuarId;
  }, [fuarId]);

  // Durumu adres çubuğuna yansıt (replace: geri tuşu her tuş vuruşunda bir adım gitmesin). Yalnızca
  // UYGULANMIŞ değerler yazılır: yazılan ama henüz debounce'a girmemiş arama/süzgeç URL'yi oynatmaz.
  useEffect(() => {
    const next = listeDurumunuUrleYaz(searchParams, {
      fuarGorunumu, arama, sayfa: page, suzgec: uygulananSuzgec,
    });
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [fuarGorunumu, arama, page, uygulananSuzgec, searchParams, setSearchParams]);

  useEffect(() => {
    let ignore = false;
    getFairs()
      .then((res) => { if (!ignore) setFuarlar(res?.data || []); })
      .catch(() => {});
    return () => { ignore = true; };
  }, [getFairs]);

  // Yalnızca fuar seçiliyken; yanıttaki fuarId ekrandakiyle eşleşmezse eski fuarın sayısı gösterilmez.
  useEffect(() => {
    if (!fuarId) return undefined;
    let ignore = false;
    getEslesmeIstatistik({ fuarId })
      .then((s) => { if (!ignore) setIstatistik(s); })
      .catch(() => { if (!ignore) setIstatistik(null); });
    return () => { ignore = true; };
  }, [fuarId, reloadToken]);

  // Arama: 400 ms bekle, tek harfle istek atma. Değer zaten uygulanmışsa (açılışta adresten gelen
  // arama dahil) dokunma — yoksa geri yüklenen sayfa numarası 1'e sıfırlanırdı.
  const uygulananArama = useRef(arama);
  useEffect(() => { uygulananArama.current = arama; }, [arama]);
  useEffect(() => {
    const t = setTimeout(() => {
      const v = aramaInput.trim();
      if (v.length === 1 || v === uygulananArama.current) return;
      setArama(v);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [aramaInput]);

  // Filtreler: kural tabanlı olanlar 1-2 sn sürebiliyor, her tıklamada istek atma.
  useEffect(() => {
    if (suzgec === uygulananSuzgec) return undefined;
    const t = setTimeout(() => {
      setUygulananSuzgec(suzgec);
      setPage(1);
    }, SUZGEC_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [suzgec, uygulananSuzgec]);

  useEffect(() => {
    if (fuarBekleniyor) return undefined; // üst çubuk fuarı gelince bu efekt yeniden çalışır
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await getKisiListesi({
          // aktifFuarId = üst çubuk fuarı (#359): Tüm Kişiler'de de satırlara kartBasim/kkd taşır ve
          // Kart basımı / KKD / "Aktif fuara göre" süzgeçlerinin fuarıdır (fuar görünümünde fuarId ile aynı).
          fuarId, aktifFuarId: ustCubukFuarId, search: arama, page, size: PAGE_SIZE, belgeler: true,
          suzgec: uygulananSuzgec,
        });
        if (ignore) return;
        // Adresteki sayfa veri azaldığı için artık yoksa son sayfaya in (boş "Kişi bulunamadı" çıkmasın).
        if (res.total > 0 && page > res.totalPages) { setPage(Math.max(1, res.totalPages)); return; }
        setSayfa(res);
      } catch (e) {
        if (!ignore) { setError(e.message); setSayfa({ records: [], total: 0, totalPages: 1 }); }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, ustCubukFuarId, fuarBekleniyor, arama, page, uygulananSuzgec, reloadToken]);

  // Detaydan dönüşte (#262) liste yüklenip çizildikten sonra kaldığı yere kaydır — bir kez.
  useEffect(() => {
    if (loading || sayfa.records.length === 0 || bekleyenKaydirma.current == null) return;
    const y = bekleyenKaydirma.current;
    bekleyenKaydirma.current = null;
    // html { scroll-behavior: smooth } (bootstrap) olduğu için "instant": geri dönüşte yukarıdan kayarak inmesin.
    requestAnimationFrame(() => window.scrollTo({ top: y, left: 0, behavior: "instant" }));
  }, [loading, sayfa.records.length]);

  const rows = sayfa.records;
  const total = sayfa.total;
  const totalPages = sayfa.totalPages;
  const gecerliSayfa = Math.min(page, totalPages);

  const fuarAdi = useMemo(() => {
    const map = new Map(fuarlar.map((f) => [Number(f.id), f.nameTR]));
    return (id) => (id ? map.get(Number(id)) || `Fuar ${id}` : "");
  }, [fuarlar]);
  // #350/#352: künye başlığının sağındaki fuar logosu (FairContext `logoUrl`); yoksa başlık fuar adını yazar.
  const fuarLogoUrl = useMemo(() => {
    const map = new Map(fuarlar.map((f) => [Number(f.id), f.logoUrl]));
    return (id) => (id ? map.get(Number(id)) || null : null);
  }, [fuarlar]);
  const fuarLabel = fuarAdi(fuarId);
  const fuarIstatistik = fuarId && istatistik?.fuarId === fuarId ? istatistik : null;
  // Yeni kişi her zaman üst çubuktaki fuara görevlendirilir (backend fuarı oturumdan alır), her iki görünümde.
  const gorevFuarId = ustCubukFuarId;

  function changeGorunum(fuar) {
    if (fuar === fuarGorunumu) return;
    setFuarGorunumu(fuar);
    setPage(1);
    // Görünüme özgü süzgeçler öbür görünümde anlamsız (uç 400 verir): sıfırla ve hemen uygula.
    //  · Tüm Kişiler'e dönüş: fuarId'ye bağlı kartDurumu / kartSeviyesi (#361) / firmaTipi.
    //  · Fuar görünümüne geçiş: "Aktif fuara göre" (#359) — fuarId'li istekte 400; o görünümde firmalar zaten
    //    o fuarın görevlendirmeleri. firmaBelgeTam / kartBasim / kkd iki görünümde de geçerli, kalır.
    const temiz = { ...suzgec };
    if (!fuar) {
      temiz.kartDurumu = "";
      temiz.kartSeviyesi = "";
      temiz.firmaTipi = "";
    } else {
      temiz.firmaBelgeAktifFuar = false;
    }
    if (Object.keys(temiz).some((k) => temiz[k] !== suzgec[k])) {
      setSuzgec(temiz);
      setUygulananSuzgec(temiz);
    }
  }

  function changeSuzgec(next) {
    setSuzgec(next);
  }

  function temizleSuzgec() {
    const bos = { ...BOS_SUZGEC, belge: {} };
    setSuzgec(bos);
    setUygulananSuzgec(bos);
    setPage(1);
  }

  function closeYeni() {
    setYeniAcik(false);
    if (searchParams.has("yeni") || searchParams.has("firmaId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("yeni"); next.delete("firmaId");
      setSearchParams(next, { replace: true });
    }
  }

  // Detaya giderken liste adresi + kaydırma konumu "geri" için bırakılır (#262). Konum bu girdinin KENDİ
  // state'ine de yazılır: tarayıcı geri tuşu Link state'ini taşımaz ama girdiyi geri getirir.
  function openDetay(url) {
    const geri = `${location.pathname}${location.search}`;
    const scrollY = Math.round(window.scrollY);
    navigate(geri, { replace: true, state: { scrollY } });
    navigate(url, { state: { from: geri, fromLabel: tx("tumPersoneller.kisiListesineDon"), scrollY } });
  }

  // Kaydedilen karar satıra yerinde işlenir; süzgeç açıksa satır süzgeçten düşebilir → yeniden çek.
  function onKimlikSaved(personId, data) {
    const patch = (r) => (r.personId === personId ? { ...r, kimlikDogrulama: { ...r.kimlikDogrulama, ...data } } : r);
    setSayfa((prev) => ({ ...prev, records: prev.records.map(patch) }));
    setKimlikHedef(null);
    if (aktifSayi > 0) setReloadToken((t) => t + 1);
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("tumPersoneller.kisiListesi")} pageTitle={tx("tumPersoneller.kisiler")} />

        <Card className="mb-3">
          <CardBody>
            <Row className="g-2 align-items-center">
              <Col lg="auto" md="auto">
                {/* Fuar düğmesi üst çubuktaki fuarı izler; üst çubukta fuar yokken kapalı (#260). */}
                <ButtonGroup>
                  <Button color={fuarGorunumu ? "light" : "primary"} className="text-nowrap" onClick={() => changeGorunum(false)}>
                    {tx("tumPersoneller.tumKisiler")}
                  </Button>
                  <Button
                    color={fuarGorunumu ? "primary" : "light"} className="text-nowrap"
                    disabled={!ustCubukFuarId} title={ustCubukFuarId ? fuarAdi(ustCubukFuarId) : tx("tumPersoneller.ustCubuktaFuarSeciliDegil")}
                    onClick={() => changeGorunum(true)}
                  >
                    {tx("tumPersoneller.fuaraEslenmis")}
                  </Button>
                </ButtonGroup>
              </Col>
              <Col lg={3} md={6}>
                <div className="position-relative">
                  <Search size={14} className="position-absolute text-muted" style={{ left: 12, top: 12 }} />
                  <Input
                    className="ps-5" value={aramaInput} placeholder={tx("tumPersoneller.adSoyadTCNo")}
                    onChange={(e) => setAramaInput(e.target.value)}
                  />
                </div>
              </Col>
              <Col lg="auto" md="auto">
                <Button color="light" className="text-nowrap" active={filtreAcik} onClick={() => setFiltreAcik((a) => !a)}>
                  <SlidersHorizontal size={15} className="me-1" />{tx("tumPersoneller.filtreler")}
                  {aktifSayi > 0 && <Badge color="primary" pill className="ms-2">{aktifSayi}</Badge>}
                </Button>
              </Col>
              <Col className="d-flex gap-2 justify-content-lg-end">
                <Button color="success" className="text-nowrap" onClick={() => setYeniAcik(true)} disabled={!gorevFuarId}>
                  <UserPlus size={15} className="me-1" />{tx("tumPersoneller.personelEkle")}
                </Button>
              </Col>
            </Row>

            <Collapse isOpen={filtreAcik}>
              <div className="border-top mt-3 pt-3">
                <KisiFiltreleri
                  suzgec={suzgec} onChange={changeSuzgec} fuarId={fuarId} aktifFuarId={ustCubukFuarId}
                  aktifSayi={aktifSayi} onTemizle={temizleSuzgec}
                />
              </div>
            </Collapse>
          </CardBody>
        </Card>

        {error && <Alert fade={false} color="danger">{error}</Alert>}

        <div className="d-flex align-items-center justify-content-between mb-2 px-1">
          <h6 className="mb-0">
            {fuarId ? fuarLabel : tx("tumPersoneller.tumKisiler")}
            {!loading && <Badge color="light" className="text-body border fw-normal ms-2">{total.toLocaleString(currentLocaleTag())}</Badge>}
            {/* #362: köşe rozeti (kart seviyesi) ve vesikalık çerçevesi (kimlik) artık ters; açıklaması "(i)"da. */}
            <PersonelLegendButton kisiListesi className="ms-2 align-middle" />
          </h6>
          <div className="d-flex align-items-center gap-3">
            {fuarIstatistik && (
              // `bilinmeyen`: 2026-09-03 öncesi görevlendirmeler, kaynağı geri doldurulamaz — iki sayaç
              // 0 okusa bile fuar boş değil, bu yüzden ayrıca yazılır.
              <div className="small text-muted">
                <strong className="text-body">{fuarIstatistik.gorevlendirme?.kisiSayisi ?? 0}</strong>{" "}{tx("tumPersoneller.gorevlendirme")}
                {" · "}<strong className="text-body">{fuarIstatistik.esleme?.kisiSayisi ?? 0}</strong>{" "}{tx("tumPersoneller.esleme")}
                {fuarIstatistik.bilinmeyen?.kisiSayisi > 0 && (
                  <>{" · "}<strong className="text-body">{fuarIstatistik.bilinmeyen.kisiSayisi.toLocaleString(currentLocaleTag())}</strong>{" "}{tx("tumPersoneller.kaynagiKayitsiz")}</>
                )}
              </div>
            )}
            {loading && rows.length > 0 && <Spinner size="sm" color="primary" />}
          </div>
        </div>

        {loading && rows.length === 0 && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && rows.length === 0 && !error && <Alert fade={false} color="secondary">{tx("tumPersoneller.kisiBulunamadi")}</Alert>}

        {rows.map((r) => {
          // Kareler, çerçeve, Kart Yazdır ve yeni sekme bağlantıları hangi firmayı konuşuyorsa Detaylar da o
          // (`onFirmaSec`: süzgeçteki firma → kararı veren → en sonuncusu); firmalar[0] firma süzgeciyle ayrışırdı.
          const detayFirma = onFirmaSec(r, uygulananSuzgec.firma?.firmaId);
          // Tüm kişiler görünümünde fuar yok: kişinin en son çalıştığı firmanın son fuarı kullanılır.
          const detayFuarId = fuarId || detayFirma?.sonFuarId || null;
          // Firması (ya da firmanın fuarı) olmayan kişi de açılır: firmasız kişi sayfası (#292).
          const detayUrl = detayFirma && detayFuarId
            ? `/Management/Firmalar/Personel/${detayFirma.firmaId}/${r.personId}?fuarId=${detayFuarId}`
            : `/Management/Kisi/Personeller/${r.personId}`;
          return (
            <PersonelKart
              key={r.personId}
              person={r}
              soluk={loading}
              firmaFiltreId={uygulananSuzgec.firma?.firmaId}
              fuarId={fuarId}
              kartFuarId={ustCubukFuarId}
              onKimlik={() => setKimlikHedef(r)}
              onDetay={() => openDetay(detayUrl)}
              onKartYazdir={() => setKartHedef(r)}
              onPanelDavet={() => setDavetHedef(r)}
              onFuaraEsle={() => setEsleHedef(r)}
              onMeslek={({ firma, fuarId: hedefFuarId }) => setMeslekHedef({ person: r, firma, fuarId: hedefFuarId })}
            />
          );
        })}

        {rows.length > 0 && (
          <div className="d-flex flex-column align-items-center p-3">
            {totalPages > 1 && (
              <Pagination>
                <PaginationItem disabled={gecerliSayfa <= 1}>
                  <PaginationLink onClick={() => setPage(1)}>{tx("tumPersoneller.ilk")}</PaginationLink>
                </PaginationItem>
                <PaginationItem disabled={gecerliSayfa <= 1}>
                  <PaginationLink previous onClick={() => setPage(gecerliSayfa - 1)} />
                </PaginationItem>
                {sayfaNumaralari(gecerliSayfa, totalPages).map((n) => (
                  <PaginationItem key={n} active={n === gecerliSayfa}>
                    <PaginationLink onClick={() => setPage(n)}>{n}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem disabled={gecerliSayfa >= totalPages}>
                  <PaginationLink next onClick={() => setPage(gecerliSayfa + 1)} />
                </PaginationItem>
                <PaginationItem disabled={gecerliSayfa >= totalPages}>
                  <PaginationLink onClick={() => setPage(totalPages)}>{tx("tumPersoneller.son")}</PaginationLink>
                </PaginationItem>
              </Pagination>
            )}
            <div className="text-muted small">
              {tx("tumPersoneller.sayfa", { gecerliSayfa })}{" "}{totalPages.toLocaleString(currentLocaleTag())}{" "}{tx("tumPersoneller.toplam")}{" "}{total.toLocaleString(currentLocaleTag())}{" "}{tx("tumPersoneller.kisi")}
            </div>
          </div>
        )}

        {kimlikHedef && (
          // #352: fotoğraf yüklenince satır (fotografVar / logo) yeniden okunur ki vesikalık listede de görünsün.
          <KimlikDogrulamaModal
            person={kimlikHedef}
            fuarAdi={ustCubukFuarId ? fuarAdi(ustCubukFuarId) : null}
            fuarLogoUrl={fuarLogoUrl(ustCubukFuarId)}
            firmaFiltreId={uygulananSuzgec.firma?.firmaId}
            onClose={() => setKimlikHedef(null)}
            onSaved={onKimlikSaved}
            onFotoDegisti={() => setReloadToken((t) => t + 1)}
          />
        )}

        {meslekHedef && (
          <MeslekDuzenleModal
            person={meslekHedef.person}
            onClose={() => setMeslekHedef(null)}
            // Satırdaki meslek/kaynak ve süzgeçler değişebilir: yerinde yama yerine yeniden çek.
            onSaved={() => { setMeslekHedef(null); setReloadToken((t) => t + 1); }}
          />
        )}

        {davetHedef && (
          <PanelDavetiModal
            personId={davetHedef.personId}
            adSoyad={`${davetHedef.name ?? ""} ${davetHedef.surname ?? ""}`.trim()}
            onClose={() => setDavetHedef(null)}
          />
        )}

        {kartHedef && ustCubukFuarId && (
          <KartYazdirModal
            person={kartHedef}
            fuarId={ustCubukFuarId}
            fuarAdi={fuarAdi(ustCubukFuarId)}
            fuarLogoUrl={fuarLogoUrl(ustCubukFuarId)}
            baslangicFirmaId={onFirmaSec(kartHedef, uygulananSuzgec.firma?.firmaId)?.firmaId}
            // Firma süzgeci açıkken karar yalnızca o firmanındır (#400); açık değilse pencere kişinin firmalarını kendisi birleştirir.
            firmaSabitId={uygulananSuzgec.firma?.firmaId}
            onClose={() => setKartHedef(null)}
            // Yığılı kimlik penceresinde yapılan iş satıra yansır ama Kart Yazdır açık kalır (#350).
            onKimlikSaved={onKimlikSaved}
            onFotoDegisti={() => setReloadToken((t) => t + 1)}
            // "Bu fuarda görevlendirilmemiş" durumundan eşlemeye geç (#261): kart penceresi kapanır, eşleme açılır.
            onFuaraEsle={() => { setEsleHedef(kartHedef); setKartHedef(null); }}
          />
        )}

        {/* Fuara Eşle (#261): hedef fuar HER ZAMAN üst çubuktaki fuar (Personel Ekle ve Kart Yazdır gibi). Kişi satırdan
            bilinir, kimlik sorgusu yok; ön dolum firması popup'ta kişinin firmalarından çıkar, satırın öndeki firması yedek. */}
        {esleHedef && ustCubukFuarId && (
          <PersonelEkleModal
            fuarId={ustCubukFuarId}
            fuarLabel={fuarAdi(ustCubukFuarId)}
            fuarLogoUrl={fuarLogoUrl(ustCubukFuarId)}
            firmayaEsle={fuarGorunumu}
            // #401/#477: satır bu fuarda başka bir firmayla zaten eşliyse (backend #476) başlık "Başka Firmaya Eşle" olur; PersonelKart'taki düğmeyle aynı koşul.
            baskaFirmayaEsle={fuarGorunumu && esleHedef.aktifFuardaGorevli === true}
            bilinenKisi={kisiSatirindanBilinenKisi(esleHedef)}
            initialFirma={onFirmaSec(esleHedef, uygulananSuzgec.firma?.firmaId)}
            onClose={() => setEsleHedef(null)}
            onDone={() => setReloadToken((t) => t + 1)}
          />
        )}

        {yeniAcik && gorevFuarId && (
          <PersonelEkleModal
            fuarId={gorevFuarId}
            fuarLabel={fuarAdi(gorevFuarId)}
            initialFirma={initialFirmaId ? { firmaId: initialFirmaId, firmaName: `Firma ${initialFirmaId}` } : null}
            onClose={closeYeni}
            onDone={() => setReloadToken((t) => t + 1)}
          />
        )}
      </Container>
    </div>
  );
}
